'use client';

import {
  IChatRoomSubscriberCandidate,
  setChatRoomSubscriber,
  setMyChatRoomSubscription,
  ThreadSide,
  useChatRoomSubscriberCandidates,
} from '@commenda/api';
import {
  Avatar,
  AvatarGroup,
  Button,
  Checkbox,
  Icons,
  MultiSelect,
} from '@commenda/kakashi';
import {
  ChatRoomMessageChannel,
  ChatRoomSubscriptionStatus,
} from '@prisma/client';
import { useMemo, useState } from 'react';

/**
 * Who is looking at the control — decides which side(s) they may edit. It's the thread
 * side the viewer sits on, plus ADMIN for Commenda staff:
 *  - CUSTOMER (enterprise app): edit company members; agents shown read-only.
 *  - AGENT (agent app): edit agent/CA-firm members; clients shown read-only.
 *  - ADMIN (commenda admin app): edit everyone.
 */
export type SubscriberViewerRole = ThreadSide | 'ADMIN';

function displayName(
  c: Pick<IChatRoomSubscriberCandidate, 'name' | 'email' | 'userId'>
) {
  return c.name ?? c.email ?? c.userId;
}

/** A rendered section. Commenda staff are split out of the agent side into their own group. */
type GroupKey = 'CUSTOMER' | 'AGENT' | 'COMMENDA_ADMIN';

function groupKeyOf(c: IChatRoomSubscriberCandidate): GroupKey {
  return c.isCommendaAdmin ? 'COMMENDA_ADMIN' : c.side;
}

/** Channel sent when editing a candidate — drives the server's per-channel edit check. */
function channelForGroup(key: GroupKey): ChatRoomMessageChannel {
  // Commenda staff route on the agent side, so they use the agent-internal channel.
  return key === 'CUSTOMER'
    ? ChatRoomMessageChannel.CUSTOMER
    : ChatRoomMessageChannel.AGENT_INTERNAL;
}

/** Fixed display order across every viewer: company side, then agent side, then Commenda. */
const GROUP_ORDER: GroupKey[] = ['CUSTOMER', 'AGENT', 'COMMENDA_ADMIN'];

function groupLabel(key: GroupKey, role: SubscriberViewerRole): string {
  if (key === 'COMMENDA_ADMIN') return 'Commenda';
  if (key === 'CUSTOMER') return role === 'CUSTOMER' ? 'My Team' : 'Client';
  return role === 'AGENT' ? 'My Team' : 'Service Provider';
}

/**
 * Edit permission by group. Only Commenda admins manage other Commenda staff;
 * otherwise a viewer manages their own side. An authorized agent (assigned firm
 * user / firm admin, per `canManageCustomerSide`) may additionally manage the
 * client side. The server is the final authority.
 */
function canEditGroup(
  key: GroupKey,
  role: SubscriberViewerRole,
  canManageCustomerSide: boolean
): boolean {
  if (role === 'ADMIN') return true;
  if (key === 'COMMENDA_ADMIN') return false;
  if (key === role) return true;
  return key === 'CUSTOMER' && role === 'AGENT' && canManageCustomerSide;
}

/**
 * Thread subscribers control (rendered inside ChatRoom's header on every app):
 * a grouped MultiSelect of subscribers with an avatar-stack trigger and side sections.
 *
 * The viewer's OWN side lists every member (a multiselect — check to subscribe); the
 * OPPOSITE side and Commenda staff list only people already subscribed and are read-only.
 * `viewerRole` decides which side is the viewer's own. The server is the final authority.
 */
export function ThreadSubscribers({
  chatRoomId,
  viewerRole,
  currentUserId,
}: {
  chatRoomId: string;
  viewerRole: SubscriberViewerRole;
  currentUserId?: string;
}) {
  // Fetch unconditionally so the trigger (avatar stack) is correct when closed.
  const { candidates, canManageCustomerSubscribers, mutate } =
    useChatRoomSubscriberCandidates(chatRoomId);

  const subscribed = candidates.filter((c) => c.status === 'SUBSCRIBED');

  const [isUpdatingSelf, setIsUpdatingSelf] = useState(false);
  const showSelfSubscribe = viewerRole === 'ADMIN' && !!currentUserId;
  const isSelfSubscribed = subscribed.some((c) => c.userId === currentUserId);

  const toggleSelfSubscription = async () => {
    setIsUpdatingSelf(true);
    try {
      await setMyChatRoomSubscription(
        chatRoomId,
        isSelfSubscribed
          ? ChatRoomSubscriptionStatus.UNSUBSCRIBED
          : ChatRoomSubscriptionStatus.SUBSCRIBED
      );
      await mutate();
    } finally {
      setIsUpdatingSelf(false);
    }
  };

  const candidateById = useMemo(
    () => new Map(candidates.map((c) => [c.userId, c] as const)),
    [candidates]
  );

  // Grouped options: an editable group lists every member; a read-only group
  // (opposite side / Commenda staff) lists only the people already subscribed.
  const options = useMemo(
    () =>
      GROUP_ORDER.flatMap((key) => {
        const editable = canEditGroup(
          key,
          viewerRole,
          canManageCustomerSubscribers
        );
        return candidates
          .filter((c) => groupKeyOf(c) === key)
          .filter((c) => editable || c.status === 'SUBSCRIBED')
          .map((c) => ({
            value: c.userId,
            label: displayName(c),
            group: groupLabel(key, viewerRole),
            disabled: !editable,
          }));
      }),
    [candidates, viewerRole, canManageCustomerSubscribers]
  );

  const selectedValues = useMemo(
    () =>
      candidates.filter((c) => c.status === 'SUBSCRIBED').map((c) => c.userId),
    [candidates]
  );

  // MultiSelect hands back the full next selection; diff it against the current
  // subscribers and write each editable change (status + side-derived channel).
  const onValueChange = async (next: string[]) => {
    const nextSet = new Set(next);
    const prevSet = new Set(selectedValues);
    const changed = candidates.filter(
      (c) =>
        nextSet.has(c.userId) !== prevSet.has(c.userId) &&
        canEditGroup(groupKeyOf(c), viewerRole, canManageCustomerSubscribers)
    );
    if (!changed.length) return;

    await Promise.all(
      changed.map((c) =>
        setChatRoomSubscriber(
          chatRoomId,
          c.userId,
          nextSet.has(c.userId) ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
          channelForGroup(groupKeyOf(c))
        )
      )
    );
    await mutate();
  };

  return (
    <div className="flex items-center gap-sm">
      {showSelfSubscribe && (
        <Button
          variant="secondary"
          size="xs"
          onClick={() => void toggleSelfSubscription()}
          isLoading={isUpdatingSelf}
          leftIcon={
            isSelfSubscribed ? (
              <Icons.BellOff01 className="size-3.5" />
            ) : (
              <Icons.Bell01 className="size-3.5" />
            )
          }
        >
          {isSelfSubscribed ? 'Unsubscribe' : 'Subscribe'}
        </Button>
      )}
      <MultiSelect
        options={options}
        defaultValue={selectedValues}
        onValueChange={onValueChange}
        hideSelectAll
        hideClearAll
        searchText="Search people…"
        notFoundText="No people to show."
        dropdownAlignment="end"
        triggerTooltip={{ content: 'Subscribers' }}
        renderTrigger={() => (
          <button
            type="button"
            aria-label="Subscribers"
            onClick={() => void mutate()}
            className="flex items-center rounded-md px-xs py-xs"
          >
            {subscribed.length ? (
              <AvatarGroup
                size="xs"
                limit={2}
                avatars={subscribed.map((c) => ({
                  name: displayName(c),
                  colorSeed: c.userId,
                }))}
              />
            ) : (
              <span className="txt-extra-small-medium text-text-tertiary">
                No subscribers
              </span>
            )}
          </button>
        )}
        renderOption={({ option, isSelected }) => {
          const c = candidateById.get(option.value);
          return (
            <div className="flex items-center gap-md w-full">
              <Checkbox
                size="sm"
                checked={isSelected}
                disabled={option.disabled}
                className="pointer-events-none"
              />
              <Avatar size="xs" name={option.label} colorSeed={option.value} />
              <span className="txt-small-medium flex-1 truncate">
                {option.label}
              </span>
              {c?.source === 'ASSIGNMENT' && (
                <span className="txt-extra-small-medium text-text-tertiary">
                  Assignee
                </span>
              )}
            </div>
          );
        }}
      />
    </div>
  );
}
